import React, { useCallback, useEffect, useState, useRef } from 'react';
import {
  Box,
  ActionIcon,
  Button,
  Group,
  Text,
  Stack,
  Overlay,
  Transition,
  Loader,
  Portal,
} from '@mantine/core';
import {
  IconX,
  IconCamera,
  IconCameraRotate,
  IconPhoto,
  IconCheck,
  IconRefresh,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { useCamera } from '../hooks/useCamera';
import { encodeMimeFor } from '../ImageCropEditor/imageAlpha';

// ============================================
// Types
// ============================================

export interface CameraCaptureProps {
  /** Whether the camera overlay is open */
  opened: boolean;
  /** Called when the user closes the overlay without capturing */
  onClose: () => void;
  /** Called with the captured/selected image blob */
  onCapture: (blob: Blob) => void;
  /** Maximum dimension for captured images. Default: 1920 */
  maxDimension?: number;
  /** JPEG quality (0-1). Default: 0.85 */
  quality?: number;
  /** Optional title shown at the top */
  title?: string;
  /** Optional hint text shown inside the framing guide */
  hint?: string;
  /** Whether to show the framing guide overlay. Default: true */
  showGuide?: boolean;
  /** Whether to show the gallery/file picker fallback button. Default: true */
  showGalleryButton?: boolean;
  /** Accepted image types for gallery picker. Default: 'image/jpeg,image/png,image/webp' */
  acceptedTypes?: string;
}

type CapturePhase = 'camera' | 'preview';

// ============================================
// Constants
// ============================================

const DEFAULT_MAX_DIMENSION = 1920;
const DEFAULT_QUALITY = 0.85;
const DEFAULT_ACCEPTED_TYPES = 'image/jpeg,image/png,image/webp';

const GUIDE_ASPECT_RATIO = 0.707; // ~A4/Letter portrait ratio (width/height)
const GUIDE_VERTICAL_FILL = 0.70; // Guide takes 70% of viewport height
const GUIDE_CORNER_RADIUS = 12;
const GUIDE_CORNER_LENGTH = 24;
const GUIDE_STROKE_WIDTH = 3;
const GUIDE_COLOR = 'rgba(255, 255, 255, 0.7)';

/**
 * Both the video feed and the preview image use the same object-fit
 * so the user sees identical framing before and after capture.
 *
 * 'cover' — fills the viewport, may crop edges (immersive, phone-like)
 * 'contain' — shows entire image with letterboxing (nothing cropped)
 *
 * We use 'cover' for camera feel, but the captured blob is the FULL
 * frame (not cropped), so the preview also uses 'cover' to match
 * what the user saw through the viewfinder.
 */
const VIEWFINDER_OBJECT_FIT = 'cover' as const;

// ============================================
// Sub-components
// ============================================

/** Framing guide overlay — shows a document-shaped rectangle with corner brackets */
const FramingGuide: React.FC<{ hint?: string }> = ({ hint }) => {
  return (
    <Box
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2,
      }}
    >
      <Box
        style={{
          position: 'relative',
          height: `${GUIDE_VERTICAL_FILL * 100}%`,
          aspectRatio: `${GUIDE_ASPECT_RATIO}`,
          maxWidth: '85%',
        }}
      >
        {/* Corner brackets via SVG */}
        <svg
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
          }}
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          fill="none"
        >
          {/* Top-left corner */}
          <path
            d={`M ${GUIDE_CORNER_LENGTH} 0 L ${GUIDE_CORNER_RADIUS} 0 Q 0 0 0 ${GUIDE_CORNER_RADIUS} L 0 ${GUIDE_CORNER_LENGTH}`}
            stroke={GUIDE_COLOR}
            strokeWidth={GUIDE_STROKE_WIDTH}
            vectorEffect="non-scaling-stroke"
          />
          {/* Top-right corner */}
          <path
            d={`M ${100 - GUIDE_CORNER_LENGTH} 0 L ${100 - GUIDE_CORNER_RADIUS} 0 Q 100 0 100 ${GUIDE_CORNER_RADIUS} L 100 ${GUIDE_CORNER_LENGTH}`}
            stroke={GUIDE_COLOR}
            strokeWidth={GUIDE_STROKE_WIDTH}
            vectorEffect="non-scaling-stroke"
          />
          {/* Bottom-left corner */}
          <path
            d={`M 0 ${100 - GUIDE_CORNER_LENGTH} L 0 ${100 - GUIDE_CORNER_RADIUS} Q 0 100 ${GUIDE_CORNER_RADIUS} 100 L ${GUIDE_CORNER_LENGTH} 100`}
            stroke={GUIDE_COLOR}
            strokeWidth={GUIDE_STROKE_WIDTH}
            vectorEffect="non-scaling-stroke"
          />
          {/* Bottom-right corner */}
          <path
            d={`M 100 ${100 - GUIDE_CORNER_LENGTH} L 100 ${100 - GUIDE_CORNER_RADIUS} Q 100 100 ${100 - GUIDE_CORNER_RADIUS} 100 L ${100 - GUIDE_CORNER_LENGTH} 100`}
            stroke={GUIDE_COLOR}
            strokeWidth={GUIDE_STROKE_WIDTH}
            vectorEffect="non-scaling-stroke"
          />
        </svg>

        {/* Hint text at the center */}
        {hint && (
          <Box
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 16,
            }}
          >
            <Text
              size="md"
              c="white"
              ta="center"
              style={{
                textShadow: '0 1px 4px rgba(0,0,0,0.6)',
                opacity: 0.8,
              }}
            >
              {hint}
            </Text>
          </Box>
        )}
      </Box>
    </Box>
  );
};

/** Preview overlay — shows the captured image with Retake / Use Photo buttons */
const PreviewOverlay: React.FC<{
  imageUrl: string;
  objectFit: 'cover' | 'contain';
  onRetake: () => void;
  onConfirm: () => void;
  retakeLabel: string;
  confirmLabel: string;
}> = ({ imageUrl, objectFit, onRetake, onConfirm, retakeLabel, confirmLabel }) => {
  return (
    <Box
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 10,
        backgroundColor: '#000',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Preview image — uses same object-fit as the viewfinder for consistency */}
      <Box
        style={{
          flex: 1,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <img
          src={imageUrl}
          alt="Captured preview"
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: objectFit,
          }}
        />
      </Box>

      {/* Action buttons */}
      <Box
        style={{
          padding: '16px 24px',
          paddingBottom: 'max(16px, env(safe-area-inset-bottom))',
        }}
      >
        <Group justify="center" gap="md">
          <Button
            variant="subtle"
            color="gray"
            size="lg"
            leftSection={<IconRefresh size={20} />}
            onClick={onRetake}
            styles={{
              root: { color: 'white' },
            }}
          >
            {retakeLabel}
          </Button>
          <Button
            variant="filled"
            size="lg"
            leftSection={<IconCheck size={20} />}
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
        </Group>
      </Box>
    </Box>
  );
};

// ============================================
// Main Component
// ============================================

export const CameraCapture: React.FC<CameraCaptureProps> = ({
  opened,
  onClose,
  onCapture,
  maxDimension = DEFAULT_MAX_DIMENSION,
  quality = DEFAULT_QUALITY,
  title,
  hint,
  showGuide = true,
  showGalleryButton = true,
  acceptedTypes = DEFAULT_ACCEPTED_TYPES,
}) => {
  const { t } = useTranslation(['common']);

  const {
    videoRef,
    isStreaming,
    isInitializing,
    error: cameraError,
    hasMultipleCameras,
    activeDeviceId,
    devices,
    start,
    stop,
    cycleCamera,
    capturePhoto,
  } = useCamera({ facingMode: 'environment' });

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [phase, setPhase] = useState<CapturePhase>('camera');
  const [capturedBlob, setCapturedBlob] = useState<Blob | null>(null);
  const [capturedUrl, setCapturedUrl] = useState<string | null>(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [imageSource, setImageSource] = useState<'camera' | 'gallery'>('camera');

  // ============================================
  // Front-camera detection for mirroring
  // ============================================

  /**
   * Detect whether the active camera is front-facing (user-facing).
   *
   * We mirror the video preview for front cameras so the user's
   * movements feel natural (like a mirror). The captured photo
   * is NOT mirrored — text on receipts stays readable.
   *
   * Detection strategy:
   * 1. Check the video track's facingMode setting (most reliable)
   * 2. Fall back to label heuristics ("front", "facetime", "user")
   * 3. Default to not mirrored (assume environment/rear camera)
   */
  const isFrontCamera = useCallback((): boolean => {
    if (!videoRef.current?.srcObject) return false;

    const stream = videoRef.current.srcObject as MediaStream;
    const track = stream.getVideoTracks()[0];

    if (!track) return false;

    // Method 1: Check track settings (works on most modern browsers)
    const settings = track.getSettings();
    if (settings.facingMode) {
      return settings.facingMode === 'user';
    }

    // Method 2: Label heuristics (fallback for desktop/older browsers)
    const label = (track.label || '').toLowerCase();
    const frontKeywords = ['front', 'facetime', 'user', 'selfie', 'face'];
    if (frontKeywords.some((kw) => label.includes(kw))) {
      return true;
    }

    // Method 3: On devices with exactly 2 cameras, if the current device
    // is the first one, it's often the front camera (especially on laptops).
    // But this is unreliable on phones where the first is usually rear.
    // So we only apply this heuristic when there's a single camera (laptop).
    if (devices.length === 1) {
      // Single camera on laptop — likely front-facing
      return true;
    }

    return false;
  }, [videoRef, devices]);

  const [mirrored, setMirrored] = useState(false);

  // Update mirror state when the stream or active device changes
  useEffect(() => {
    if (isStreaming) {
      // Small delay to let the track settings populate
      const timer = setTimeout(() => {
        setMirrored(isFrontCamera());
      }, 100);
      return () => clearTimeout(timer);
    } else {
      setMirrored(false);
    }

    return;
  }, [isStreaming, activeDeviceId, isFrontCamera]);

  // ============================================
  // Lifecycle: start/stop camera with open state
  // ============================================

  useEffect(() => {
    if (opened) {
      setPhase('camera');
      setCapturedBlob(null);
      setCapturedUrl(null);
      start();
    } else {
      stop();
    }

    return () => {
      stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  // Cleanup object URL when it changes
  useEffect(() => {
    return () => {
      if (capturedUrl) {
        URL.revokeObjectURL(capturedUrl);
      }
    };
  }, [capturedUrl]);

  // ============================================
  // Handlers
  // ============================================

  const handleCapture = useCallback(async () => {
    setIsCapturing(true);

    const blob = await capturePhoto({ maxDimension, quality });

    if (blob) {
      const url = URL.createObjectURL(blob);
      setCapturedBlob(blob);
      setCapturedUrl(url);
      setImageSource('camera');
      setPhase('preview');
    }

    setIsCapturing(false);
  }, [capturePhoto, maxDimension, quality]);

  const handleRetake = useCallback(() => {
    if (capturedUrl) {
      URL.revokeObjectURL(capturedUrl);
    }
    setCapturedBlob(null);
    setCapturedUrl(null);
    setPhase('camera');

    // Restart camera if it was stopped
    if (!isStreaming) {
      start();
    }
  }, [capturedUrl, isStreaming, start]);

  const handleConfirm = useCallback(() => {
    if (capturedBlob) {
      onCapture(capturedBlob);
    }
  }, [capturedBlob, onCapture]);

  const handleClose = useCallback(() => {
    stop();
    if (capturedUrl) {
      URL.revokeObjectURL(capturedUrl);
    }
    setCapturedBlob(null);
    setCapturedUrl(null);
    setPhase('camera');
    onClose();
  }, [stop, capturedUrl, onClose]);

  const handleGalleryClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];

      // Reset so same file can be re-selected
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      if (!file || !file.type.startsWith('image/')) {
        return;
      }

      // Resize the selected file using canvas
      const resizedBlob = await resizeImageFile(file, maxDimension, quality);

      if (resizedBlob) {
        const url = URL.createObjectURL(resizedBlob);
        setCapturedBlob(resizedBlob);
        setCapturedUrl(url);
        setImageSource('gallery');
        setPhase('preview');
      }
    },
    [maxDimension, quality]
  );

  const handleCycleCamera = useCallback(() => {
    cycleCamera();
  }, [cycleCamera]);

  // ============================================
  // Render
  // ============================================

  if (!opened) {
    return null;
  }

  const showCameraError = cameraError && !isInitializing;
  const displayTitle = title || t('common:camera.title', 'Camera');

  return (
    <Portal>
      <Box
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 1000,
          backgroundColor: '#000',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* ---- Top bar ---- */}
        <Box
          style={{
            position: 'relative',
            zIndex: 5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 16px',
            paddingTop: 'max(12px, env(safe-area-inset-top))',
          }}
        >


          <Text size="md" fw={500} c="white" style={{ textShadow: '0 1px 3px rgba(0,0,0,0.5)' }}>
            {displayTitle}
          </Text>

          {/* Camera switch button (or spacer for alignment) */}
          {hasMultipleCameras ? (
            <ActionIcon
              variant="subtle"
              color="gray"
              size="lg"
              onClick={handleCycleCamera}
              aria-label={t('common:camera.switchCamera', 'Switch camera')}
              styles={{ root: { color: 'white' } }}
            >
              <IconCameraRotate size={24} />
            </ActionIcon>
          ) : (
            <Box style={{ width: 42 }} />
          )}

          <ActionIcon
            variant="subtle"
            color="gray"
            size="lg"
            onClick={handleClose}
            aria-label={t('common:actions.close', 'Close')}
            styles={{ root: { color: 'white' } }}
          >
            <IconX size={24} />
          </ActionIcon>
        </Box>

        {/* ---- Viewfinder area ---- */}
        <Box
          style={{
            flex: 1,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Video feed */}
          <video
            ref={videoRef}
            playsInline
            muted
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: VIEWFINDER_OBJECT_FIT,
              // Mirror the preview for front-facing cameras so movements
              // feel natural. The captured photo is NOT mirrored.
              transform: mirrored ? 'scaleX(-1)' : 'none',
            }}
          />

          {/* Semi-transparent overlay outside the guide area */}
          {isStreaming && phase === 'camera' && (
            <Overlay
              color="#000"
              backgroundOpacity={0.3}
              style={{ zIndex: 1 }}
            />
          )}

          {/* Framing guide */}
          {isStreaming && showGuide && phase === 'camera' && (
            <FramingGuide hint={hint} />
          )}

          {/* Loading state */}
          {isInitializing && (
            <Box
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 3,
              }}
            >
              <Stack align="center" gap="sm">
                <Loader color="white" size="md" />
                <Text size="md" c="white">
                  {t('common:camera.initializing', 'Starting camera...')}
                </Text>
              </Stack>
            </Box>
          )}

          {/* Camera error state */}
          {showCameraError && (
            <Box
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 3,
                padding: 24,
              }}
            >
              <Stack align="center" gap="md" style={{ maxWidth: 320 }}>
                <IconCamera size={48} color="white" style={{ opacity: 0.5 }} />
                <Text size="md" c="white" ta="center">
                  {t(`common:camera.errors.${cameraError}`, 'Unable to access camera.')}
                </Text>
                {showGalleryButton && (
                  <Button
                    variant="light"
                    leftSection={<IconPhoto size={18} />}
                    onClick={handleGalleryClick}
                  >
                    {t('common:camera.chooseFromGallery', 'Choose from gallery')}
                  </Button>
                )}
              </Stack>
            </Box>
          )}

          {/* Preview overlay */}
          <Transition mounted={phase === 'preview' && !!capturedUrl} transition="fade" duration={200}>
            {(styles) => (
              <Box style={styles}>
                <PreviewOverlay
                  imageUrl={capturedUrl!}
                  onRetake={handleRetake}
                  onConfirm={handleConfirm}
                  objectFit={imageSource === 'camera' ? 'cover' : 'contain'}
                  retakeLabel={t('common:camera.retake', 'Retake')}
                  confirmLabel={t('common:camera.usePhoto', 'Use Photo')}
                />
              </Box>
            )}
          </Transition>
        </Box>

        {/* ---- Bottom controls (camera phase only) ---- */}
        {phase === 'camera' && (
          <Box
            style={{
              position: 'relative',
              zIndex: 5,
              padding: '20px 24px',
              paddingBottom: 'max(20px, env(safe-area-inset-bottom))',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 32,
            }}
          >
            {/* Gallery button */}
            {showGalleryButton ? (
              <ActionIcon
                variant="subtle"
                color="gray"
                size="xl"
                onClick={handleGalleryClick}
                aria-label={t('common:camera.chooseFromGallery', 'Choose from gallery')}
                styles={{ root: { color: 'white' } }}
              >
                <IconPhoto size={28} />
              </ActionIcon>
            ) : (
              <Box style={{ width: 42 }} />
            )}

            {/* Shutter button */}
            <ActionIcon
              variant="filled"
              color="white"
              size={72}
              radius="50%"
              onClick={handleCapture}
              disabled={!isStreaming || isCapturing}
              aria-label={t('common:camera.capture', 'Take photo')}
              styles={{
                root: {
                  border: '4px solid rgba(255,255,255,0.8)',
                  backgroundColor: isCapturing ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.9)',
                  color: '#000',
                  transition: 'transform 0.1s ease, background-color 0.15s ease',
                  '&:active': {
                    transform: 'scale(0.92)',
                  },
                },
              }}
            >
              {isCapturing ? <Loader size="sm" color="dark" /> : <IconCamera size={32} />}
            </ActionIcon>

            {/* Spacer for centering */}
            <Box style={{ width: 42 }} />
          </Box>
        )}

        {/* Hidden file input for gallery fallback */}
        <input
          ref={fileInputRef}
          type="file"
          accept={acceptedTypes}
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
      </Box>
    </Portal>
  );
};

// ============================================
// Utility: Resize an image File via canvas
// ============================================

async function resizeImageFile(
  file: File,
  maxDimension: number,
  quality: number
): Promise<Blob | null> {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);

      let { width, height } = img;

      // Scale down if needed
      if (width > maxDimension || height > maxDimension) {
        if (width >= height) {
          height = Math.round((height / width) * maxDimension);
          width = maxDimension;
        } else {
          width = Math.round((width / height) * maxDimension);
          height = maxDimension;
        }
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      if (!ctx) {
        resolve(null);
        return;
      }

      ctx.drawImage(img, 0, 0, width, height);
      // ⚠ A transparent picture stays a PNG (#135): written as a JPEG here, every transparent pixel turned black BEFORE
      //   the crop editor ever saw it. The pixels decide, not the file's declared type.
      canvas.toBlob(
        (blob) => resolve(blob),
        encodeMimeFor(ctx, width, height),
        quality
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };

    img.src = url;
  });
}